import { useState, useEffect, useRef, useCallback } from 'react'
import * as d3 from 'd3'
import { GraphData } from '@/hooks/useTaxonomyRun'
import { GraphLegend } from './GraphLegend'
import { GraphPopover, PopoverData } from './GraphPopover'

// ── Colour palette — deterministic per intent_name ──────────────────
const PALETTE = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#14b8a6']

function hashString(s: string): number {
  let hash = 0
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash)
}

function getColour(intentName: string): string {
  return PALETTE[hashString(intentName) % PALETTE.length]
}

// ── d3 simulation node type ─────────────────────────────────────────
interface SimNode extends d3.SimulationNodeDatum {
  name: string
  description: string
  example: string
  intent_name: string
  approved: string
}

interface SimLink extends d3.SimulationLinkDatum<SimNode> {
  weight: number
}

// ── Edge stroke width: lerp between 1px and 4px based on weight ─────
function edgeWidth(weight: number): number {
  return 1 + weight * 3   // weight 0→1 maps to 1px→4px
}

interface EntityGraphProps {
  graphData: GraphData
}

export function EntityGraph({ graphData }: EntityGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef       = useRef<SVGSVGElement>(null)
  const simRef       = useRef<d3.Simulation<SimNode, SimLink> | null>(null)

  // ── State: node positions (updated on each sim tick) ──
  const [positions, setPositions] = useState<Map<string, { x: number; y: number }>>(new Map())

  // ── State: min-weight filter ──
  const [minWeight, setMinWeight] = useState(0)

  // ── State: popover ──
  const [popover, setPopover] = useState<PopoverData | null>(null)

  // ── Derived: colour map for legend ──
  const colourMap = new Map<string, string>()
  graphData.nodes.forEach(n => {
    if (!colourMap.has(n.intent_name)) {
      colourMap.set(n.intent_name, getColour(n.intent_name))
    }
  })

  // ── Derived: filtered edges + dimmed nodes ──
  const filteredEdges = graphData.edges.filter(e => e.weight >= minWeight)
  const activeNodeNames = new Set<string>()
  filteredEdges.forEach(e => { activeNodeNames.add(e.source); activeNodeNames.add(e.target) })

  // ── Build node lookup (for popover edge count) ──
  const nodeMap = new Map(graphData.nodes.map(n => [n.name, n]))

  // Edge count per node (based on FILTERED edges)
  const edgeCounts = new Map<string, number>()
  filteredEdges.forEach(e => {
    edgeCounts.set(e.source, (edgeCounts.get(e.source) || 0) + 1)
    edgeCounts.set(e.target, (edgeCounts.get(e.target) || 0) + 1)
  })

  // ── Initialize / update simulation ──────────────────────────────
  useEffect(() => {
    if (graphData.nodes.length === 0) return

    const width  = containerRef.current?.clientWidth  || 600
    const height = containerRef.current?.clientHeight || 400

    // Build sim nodes
    const nodes: SimNode[] = graphData.nodes.map(n => ({
      ...n,
      // Seed positions in the centre so they don't fly in from (0,0)
      x: width  / 2 + (Math.random() - 0.5) * 100,
      y: height / 2 + (Math.random() - 0.5) * 100,
    }))

    // Build sim links — source/target are node name strings; d3 resolves them
    const links: SimLink[] = graphData.edges.map(e => ({
      source: e.source,
      target: e.target,
      weight: e.weight,
    })) as any   // d3 mutates these to hold node refs after fit

    const sim = d3.forceSimulation<SimNode, SimLink>(nodes)
      .force('link',      d3.forceLink<SimNode, SimLink>(links).id(d => d.name).distance(120))
      .force('charge',    d3.forceManyBody<SimNode>().strength(-200))
      .force('center',    d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide<SimNode>().radius(30))
      .alpha(0.3)
      .alphaDecay(0.02)

    // On each tick, flush positions into React state
    sim.on('tick', () => {
      const pos = new Map<string, { x: number; y: number }>()
      nodes.forEach(n => pos.set(n.name, { x: n.x!, y: n.y! }))
      setPositions(pos)
    })

    simRef.current = sim

    // Cleanup on unmount or data change
    return () => { sim.stop() }
  }, [graphData])

  // ── Drag handlers ──────────────────────────────────────────────────
  const onDragStart = useCallback((e: React.MouseEvent, nodeName: string) => {
    e.preventDefault()
    const sim = simRef.current
    if (!sim) return
    const node = sim.nodes().find(n => n.name === nodeName)
    if (!node) return

    node.fx = node.x
    node.fy = node.y
    sim.alphaTarget(0.3).restart()

    // Store which node is being dragged — use a ref to avoid re-renders
    ;(sim as any).__dragging = node
  }, [])

  const onDragMove = useCallback((e: React.MouseEvent) => {
    const sim = simRef.current
    const node = sim && (sim as any).__dragging
    if (!node || !containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    node.fx = e.clientX - rect.left
    node.fy = e.clientY - rect.top
  }, [])

  const onDragEnd = useCallback(() => {
    const sim = simRef.current
    const node = sim && (sim as any).__dragging
    if (node) {
      node.fx = null
      node.fy = null
      ;(sim as any).__dragging = null
    }
  }, [])

  // ── Popover helpers ──────────────────────────────────────────────
  const showNodePopover = useCallback((e: React.MouseEvent, nodeName: string) => {
    const node = nodeMap.get(nodeName)
    if (!node || !containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    setPopover({
      kind: 'node',
      node,
      edgeCount: edgeCounts.get(nodeName) || 0,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
  }, [nodeMap, edgeCounts])

  const showEdgePopover = useCallback((e: React.MouseEvent, source: string, target: string, weight: number) => {
    const sNode = nodeMap.get(source)
    const tNode = nodeMap.get(target)
    if (!sNode || !tNode || !containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    setPopover({
      kind: 'edge',
      sourceNode: sNode,
      targetNode: tNode,
      weight,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
  }, [nodeMap])

  // ── Zero-relations empty state ──────────────────────────────────
  if (graphData.nodes.length === 0 || graphData.edges.length === 0) {
    return (
      <div className="py-16 text-center text-gray-400 text-sm">
        No relationships detected for this knowledge base.
      </div>
    )
  }

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div className="mt-2">
      {/* Min-weight filter */}
      <div className="flex items-center gap-3 px-1 mb-2">
        <span className="text-xs text-gray-500 shrink-0">Min weight:</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.1}
          value={minWeight}
          onChange={e => setMinWeight(parseFloat(e.target.value))}
          className="flex-1 accent-blue-600"
        />
        <span className="text-xs text-gray-500 w-8 text-right">{minWeight.toFixed(1)}</span>
      </div>

      {/* Graph container — position: relative for popover + legend */}
      <div
        ref={containerRef}
        className="relative w-full border rounded-lg bg-gray-50 overflow-hidden"
        style={{ height: 400 }}
        onMouseMove={onDragMove}
        onMouseUp={onDragEnd}
        onMouseLeave={() => { onDragEnd(); setPopover(null) }}
      >
        {/* Legend — top-left, absolute */}
        <GraphLegend colourMap={colourMap} />

        {/* SVG */}
        <svg ref={svgRef} className="w-full h-full">
          {/* Edges */}
          {filteredEdges.map((edge, idx) => {
            const srcPos = positions.get(edge.source)
            const tgtPos = positions.get(edge.target)
            if (!srcPos || !tgtPos) return null
            return (
              <line
                key={`edge-${idx}`}
                x1={srcPos.x} y1={srcPos.y}
                x2={tgtPos.x} y2={tgtPos.y}
                stroke="#cbd5e1"
                strokeWidth={edgeWidth(edge.weight)}
                strokeLinecap="round"
                className="cursor-pointer hover:stroke-blue-400 transition-colors"
                onMouseEnter={e => showEdgePopover(e, edge.source, edge.target, edge.weight)}
                onMouseLeave={() => setPopover(null)}
              />
            )
          })}

          {/* Nodes */}
          {graphData.nodes.map(node => {
            const pos = positions.get(node.name)
            if (!pos) return null
            const colour = getColour(node.intent_name)
            const dimmed = filteredEdges.length > 0 && !activeNodeNames.has(node.name)
            return (
              <g key={node.name} style={{ opacity: dimmed ? 0.3 : 1 }}>
                {/* Hit area — slightly larger circle for easier hover/drag */}
                <circle
                  cx={pos.x} cy={pos.y} r={22}
                  fill="transparent"
                  className="cursor-grab active:cursor-grabbing"
                  onMouseDown={e => onDragStart(e, node.name)}
                  onMouseEnter={e => showNodePopover(e, node.name)}
                  onMouseLeave={() => setPopover(null)}
                />
                {/* Visible circle */}
                <circle
                  cx={pos.x} cy={pos.y} r={18}
                  fill={colour}
                  stroke="white"
                  strokeWidth={2}
                  className="pointer-events-none"
                />
                {/* Label below */}
                <text
                  x={pos.x} y={pos.y + 30}
                  textAnchor="middle"
                  className="text-xs fill-gray-600 pointer-events-none select-none"
                  fontSize={10}
                >
                  {node.name.replace(/_/g, ' ')}
                </text>
              </g>
            )
          })}
        </svg>

        {/* Popover — absolute, rendered by GraphPopover */}
        <GraphPopover popover={popover} />
      </div>
    </div>
  )
}
